/**
 * Opportunities Page
 * Displays all government contracting opportunities with search and filtering.
 * Rendered inside OpportunitiesLayout which provides tab navigation.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { PageBody, PageHero, PageToolbar } from '../../components/layout';
import { useDebounce } from '../../hooks/useDebounce';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { OpportunityFilters } from './components/OpportunityFilters';
import { OpportunityList } from './components/OpportunityList';
import { QuickFilters } from './components/QuickFilters';
const SEARCH_DEBOUNCE_MS = 300;
export function OpportunitiesPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState({});
    const [quickFilters, setQuickFilters] = useState({
        urgency: 'all',
        setAside: null,
        source: null,
        saved: false
    });
    const [viewMode, setViewMode] = useState('list');
    const [totalCount, setTotalCount] = useState(0);
    const [filteredCount, setFilteredCount] = useState(undefined);
    const debouncedSearch = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);
    // PageHero is the inline (Specra) header, so hide the legacy global top-bar
    // header and the breadcrumb bar while this page is mounted.
    const setShowBreadcrumbs = useLayoutStore((state)=>state.setShowBreadcrumbs);
    const setShowHeader = useLayoutStore((state)=>state.setShowHeader);
    useEffect(()=>{
        setShowBreadcrumbs(false);
        setShowHeader(false);
        return ()=>{
            setShowBreadcrumbs(true);
            setShowHeader(true);
        };
    }, [
        setShowBreadcrumbs,
        setShowHeader
    ]);
    const handleSearch = (value)=>{
        setSearchQuery(value);
    };
    const handleFiltersChange = (newFilters)=>{
        setFilters(newFilters);
    };
    // Count active advanced (dialog) filters so the quick bar can surface and
    // clear them alongside the chips. Exclude the dialog's setAsideType when a
    // quick set-aside chip is active — the chip wins (see OpportunityList), so
    // the dialog value isn't actually applied and shouldn't inflate the count.
    const dialogFilterCount = Object.entries(filters).filter(([key, v])=>{
        if (v === undefined || v === '') return false;
        if (key === 'setAsideType' && quickFilters.setAside !== null) return false;
        return true;
    }).length;
    const clearDialogFilters = ()=>{
        setFilters({});
    };
    const clearAllFilters = ()=>{
        setFilters({});
        setQuickFilters({
            urgency: 'all',
            setAside: null,
            source: null,
            saved: false
        });
    };
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.opportunities.page,
        children: /*#__PURE__*/ _jsxs(PageBody, {
            children: [
                /*#__PURE__*/ _jsx(PageHero, {
                    title: "Government Opportunities",
                    subtitle: "Browse available contracting opportunities"
                }),
                /*#__PURE__*/ _jsxs(PageToolbar, {
                    children: [
                        /*#__PURE__*/ _jsx(PageToolbar.Search, {
                            value: searchQuery,
                            onChange: handleSearch,
                            placeholder: "Search opportunities by title, description, or agency...",
                            className: "flex-1 max-w-none",
                            dataTestid: Testids.opportunities.searchInput
                        }),
                        /*#__PURE__*/ _jsx(OpportunityFilters, {
                            filters: filters,
                            onFiltersChange: handleFiltersChange
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(QuickFilters, {
                    filters: quickFilters,
                    onFilterChange: setQuickFilters,
                    viewMode: viewMode,
                    onViewModeChange: setViewMode,
                    totalCount: totalCount,
                    filteredCount: filteredCount,
                    dialogFilterCount: dialogFilterCount,
                    onClearAll: clearAllFilters,
                    onClearDialogFilters: clearDialogFilters
                }),
                /*#__PURE__*/ _jsx(OpportunityList, {
                    search: debouncedSearch,
                    filters: filters,
                    quickFilters: quickFilters,
                    viewMode: viewMode,
                    onTotalChange: setTotalCount,
                    onFilteredCountChange: setFilteredCount
                })
            ]
        })
    });
}
